import { AuthContext } from '@urbanx/agx.auth.client';
import { useContext, useEffect, useState } from 'react';
import {
  ContractActionRequestContractAction,
  DocumentTypes,
  StaffGetContractSettlementResponseSettlementType as SettlementType,
  StaffUpdateContractSettlementRequestSettlementType,
  type StaffUpdateContractSettlementRequest,
  UploadedDocument,
  useInvalidateUseStaffGetContract,
  useInvalidateUseStaffGetContractSettlement,
  usePerformContractAction,
  useStaffGetContractSettlement,
  useStaffUpdateContractSettlement,
} from '@urbanx/agx.campaigns.client';
import { useInvalidateUseGetAutopilotTasksForCampaign } from '@urbanx/agx.activities.client';
import { AxiosProgressEvent, GenericAbortSignal } from 'axios';
import { uploadFile } from 'services';
import { toDateOnlyString } from '@urbanx/agx.ui-components2';
import {
  buildSettlementContractDocuments,
  getInitialSettlementDate,
  hasPersistedDocumentBeenRemoved,
} from './settlementFormState';

interface Props {
  dialogOpenChangeHandler: (value: boolean) => void;
  contractId?: string;
  campaignId?: string;
  isOpen: boolean;
  onTasksInvalidated?: () => void;
}

const isFile = (file: UploadedDocument | File | undefined): file is File =>
  typeof File !== 'undefined' && file instanceof File;

export const useMarkedAsSettled = ({
  contractId,
  campaignId,
  isOpen,
  dialogOpenChangeHandler,
  onTasksInvalidated,
}: Props) => {
  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  });
  const { getAuthToken } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();
  const invalidateContract = useInvalidateUseStaffGetContract();
  const invalidateContractSettlement = useInvalidateUseStaffGetContractSettlement();
  const { performContractAction } = usePerformContractAction({
    onSuccess: () => {},
    onError: () => {},
  });
  const { data: settlement } = useStaffGetContractSettlement(
    campaignId ?? '',
    contractId ?? '',
    !!campaignId && !!contractId
  );
  const [settlementDate, setSettlementDate] = useState<Date | null>(null);
  const [settlementType, setSettlementType] = useState(SettlementType.Pexa);
  const [vendorConfirmationFiles, setVendorConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const [buyerConfirmationFiles, setBuyerConfirmationFiles] = useState<(UploadedDocument | File)[]>(
    []
  );
  const [pexaConfirmationFiles, setPexaConfirmationFiles] = useState<(UploadedDocument | File)[]>(
    []
  );

  const buildRenamedFormData = (formData: FormData) => {
    const file = formData.get('File');
    if (!file || typeof File === 'undefined' || !(file instanceof File)) {
      return formData;
    }

    const renamedFile = new File([file], `${contractId}/${file.name}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
    const renamedFormData = new FormData();
    renamedFormData.append('File', renamedFile);
    return renamedFormData;
  };

  const onUploadFile = async (
    authToken: string,
    file: string | FormData,
    uploadCampaignId: string,
    uploadedFormType: DocumentTypes,
    uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    abortSignal?: GenericAbortSignal,
    uploadContractId?: string
  ) =>
    uploadFile(
      authToken,
      typeof FormData !== 'undefined' && file instanceof FormData
        ? buildRenamedFormData(file)
        : file,
      uploadCampaignId,
      uploadedFormType,
      uploadProgress ?? null,
      abortSignal ?? null,
      undefined,
      uploadContractId
    );

  useEffect(() => {
    if (!isOpen) return;

    setSettlementDate(getInitialSettlementDate(settlement?.actualSettlementDate));
    setSettlementType(settlement?.settlementType ?? SettlementType.Pexa);
    setVendorConfirmationFiles(
      settlement?.vendorSettlementConfirmation ? [settlement.vendorSettlementConfirmation] : []
    );
    setBuyerConfirmationFiles(
      settlement?.buyerSettlementConfirmation ? [settlement.buyerSettlementConfirmation] : []
    );
    setPexaConfirmationFiles(
      settlement?.pexaSettlementConfirmation ? [settlement.pexaSettlementConfirmation] : []
    );
  }, [isOpen, settlement]);

  const hasRequiredConfirmation =
    settlementType === SettlementType.Pexa
      ? pexaConfirmationFiles.length > 0
      : vendorConfirmationFiles.length > 0 && buyerConfirmationFiles.length > 0;
  const canComplete = settlementDate != null && hasRequiredConfirmation;

  const uploadSelectedFile = async (
    authToken: string,
    documentType: DocumentTypes,
    selectedFile?: UploadedDocument | File
  ) => {
    if (!isFile(selectedFile) || !campaignId) return;

    const formData = new FormData();
    formData.append('File', selectedFile);
    const uploadedDocument = await onUploadFile(
      authToken,
      formData,
      campaignId,
      documentType,
      undefined,
      undefined,
      contractId
    );
    if (!uploadedDocument) {
      throw new Error(`Failed to upload ${documentType}`);
    }
  };

  const persistSettlementChanges = async (
    refreshWhenUnchanged = false,
    markContractAsSettled = false
  ) => {
    if (!campaignId || !contractId) return;

    setIsSubmitting(true);
    try {
      const selectedSettlementDate = toDateOnlyString(settlementDate);
      const currentSettlementDate = toDateOnlyString(settlement?.actualSettlementDate);
      const settlementDateDirty = selectedSettlementDate !== currentSettlementDate;
      const settlementTypeDirty = settlementType !== settlement?.settlementType;
      const selectedFiles = [
        {
          file: pexaConfirmationFiles[0],
          documentType: DocumentTypes.PexaSettlementConfirmation,
        },
        {
          file: vendorConfirmationFiles[0],
          documentType: DocumentTypes.VendorSettlementConfirmation,
        },
        {
          file: buyerConfirmationFiles[0],
          documentType: DocumentTypes.BuyerSettlementConfirmation,
        },
      ];
      const filesToUpload = selectedFiles.filter(({ file }) => isFile(file));
      const confirmationRemoved =
        hasPersistedDocumentBeenRemoved(
          vendorConfirmationFiles[0],
          settlement?.vendorSettlementConfirmation
        ) ||
        hasPersistedDocumentBeenRemoved(
          buyerConfirmationFiles[0],
          settlement?.buyerSettlementConfirmation
        ) ||
        hasPersistedDocumentBeenRemoved(
          pexaConfirmationFiles[0],
          settlement?.pexaSettlementConfirmation
        );
      const hasChanges =
        settlementDateDirty ||
        settlementTypeDirty ||
        confirmationRemoved ||
        filesToUpload.length > 0;
      const authToken = filesToUpload.length > 0 ? await getAuthToken() : undefined;
      if (filesToUpload.length > 0 && !authToken) return;

      if (settlementDateDirty || settlementTypeDirty || confirmationRemoved) {
        const body: StaffUpdateContractSettlementRequest = {};
        if (settlementDateDirty) body.actualSettlementDate = selectedSettlementDate;
        if (settlementTypeDirty) {
          body.settlementType =
            settlementType === SettlementType.Pexa
              ? StaffUpdateContractSettlementRequestSettlementType.Pexa
              : StaffUpdateContractSettlementRequestSettlementType.InPerson;
        }
        if (confirmationRemoved) {
          body.contractDocuments = buildSettlementContractDocuments({
            vendorConfirmation: vendorConfirmationFiles[0],
            originalVendorConfirmation: settlement?.vendorSettlementConfirmation,
            buyerConfirmation: buyerConfirmationFiles[0],
            originalBuyerConfirmation: settlement?.buyerSettlementConfirmation,
            pexaConfirmation: pexaConfirmationFiles[0],
            originalPexaConfirmation: settlement?.pexaSettlementConfirmation,
          });
        }
        await staffUpdateContractSettlement({ campaignId, contractId, body });
      }

      if (authToken) {
        for (const { file, documentType } of filesToUpload) {
          await uploadSelectedFile(authToken, documentType, file);
        }
      }

      if (hasChanges || refreshWhenUnchanged) {
        await invalidateContractSettlement(campaignId, contractId);
        await invalidateTasks(campaignId, contractId);
        onTasksInvalidated?.();
      }

      if (markContractAsSettled) {
        await performContractAction({
          campaignId,
          contractId,
          contractAction: ContractActionRequestContractAction.MarkSettled,
        });
        await invalidateContract(contractId);
        await invalidateTasks(campaignId, contractId);
        onTasksInvalidated?.();
      }

      dialogOpenChangeHandler(false);
      clearSelection();
    } finally {
      setIsSubmitting(false);
    }
  };

  const onComplete = async () => {
    if (isSubmitting || !canComplete) return;
    await persistSettlementChanges(true, true);
  };

  const clearSelection = () => {
    setSettlementDate(null);
    setSettlementType(SettlementType.Pexa);
    setVendorConfirmationFiles([]);
    setBuyerConfirmationFiles([]);
    setPexaConfirmationFiles([]);
  };

  return {
    getAuthToken,
    onUploadFile,
    settlementDate,
    setSettlementDate,
    settlementType,
    setSettlementType,
    vendorConfirmationFiles,
    setVendorConfirmationFiles,
    buyerConfirmationFiles,
    setBuyerConfirmationFiles,
    pexaConfirmationFiles,
    setPexaConfirmationFiles,
    isSubmitting,
    canComplete,
    onComplete,
    clearSelection,
    onSaveAndExit: () => persistSettlementChanges(),
  };
};
